<!-- 
  * FileSearchFilter Component
  * 
  * Provides search and filtering functionality for SharePoint files:
  * - Search files by name
  * - Filter files by file type
  * - Option to search site-wide
  * - Automatically searches site-wide when no local files are present
  * 
  * Used within SharePointSelectorModal.vue when isSelectingFiles is true
-->
<template>
  <div class="file-search-filter mb-2">
    <div class="d-flex align-items-center">
      <!-- File name search -->
      <div class="flex-grow-1 mr-1">
        <b-input-group 
          size="sm" 
          class="compact-search"
        >
          <b-input-group-prepend>
            <b-input-group-text class="px-2">
              <font-awesome-icon :icon="['fas', 'search']" />
            </b-input-group-text>
          </b-input-group-prepend>
          <b-form-input
            id="fileNameSearch"
            v-model="searchTerm"
            :placeholder="totalCount > 0 ? 'Search files...' : 'Search entire site...'"
            class="compact-input"
            @input="onSearchChange"
          />
          <b-input-group-append v-if="isSearching">
            <b-button 
              variant="outline-secondary" 
              size="sm"
              class="px-2"
              disabled
            >
              <font-awesome-icon 
                :icon="['fas', 'spinner']" 
                spin 
              />
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </div>

      <!-- File type filter -->
      <div class="flex-shrink-0">
        <b-dropdown 
          size="sm" 
          variant="outline-secondary"
          toggle-class="px-1 py-1 compact-dropdown"
          no-caret
          right
          boundary="viewport"
        >
          <template #button-content>
            <font-awesome-icon 
              :icon="['fas', 'filter']" 
              size="sm" 
            />
          </template>
          <b-dropdown-item 
            :active="selectedFileType === ''"
            class="small"
            @click="selectFileType('')"
          >
            All types
          </b-dropdown-item>
          <b-dropdown-divider v-if="availableFileTypes.length > 0" />
          <b-dropdown-item
            v-for="fileType in availableFileTypes"
            :active="selectedFileType === fileType.extension"
            class="small"
            @click="selectFileType(fileType.extension)"
          >
            {{ fileType.label }} ({{ fileType.count }})
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </div>

    <!-- Active filters summary -->
    <div 
      v-if="hasActiveFilters" 
      class="mt-1 pt-1 border-top small text-muted"
    >
      <div>
        <span v-if="searchTerm">
          <strong>Search:</strong> "{{ searchTerm }}"
          <span 
            v-if="isSiteWideSearch" 
            class="text-primary ml-1"
          >
            (site-wide)
          </span>
          <button 
            type="button" 
            class="btn btn-link p-0 ml-1 text-muted filter-clear-btn"
            title="Clear search"
            @click="clearSearch"
          >
            <font-awesome-icon :icon="['fas', 'times']" />
          </button>
        </span>
        <span
          v-if="searchTerm && selectedFileType"
          class="mx-1"
        > | </span>
        <span v-if="selectedFileType">
          <strong>Type:</strong> {{ getFileTypeLabel(selectedFileType) }}
          <button 
            type="button" 
            class="btn btn-link p-0 ml-1 text-muted filter-clear-btn"
            title="Clear file type filter"
            @click="clearFileTypeFilter"
          >
            <font-awesome-icon :icon="['fas', 'times']" />
          </button>
        </span>
        <span class="ml-1">
          <template v-if="isSiteWideSearch">
            ({{ filteredCount }} of {{ totalCount }} files in site)
          </template>
          <template v-else-if="totalCount > 0">
            ({{ filteredCount }} of {{ totalCount }} files in {{ currentLocationName }})
          </template>
          <template v-else>
            (no files in {{ currentLocationName }})
          </template>
        </span>
      </div>
    </div>

    <!-- Site-wide search expansion option -->
    <div 
      v-if="searchTerm && enableSiteSearch && !siteSearchModeEnabled && !isSearching && files.length > 0"
      class="mt-1"
    >
      <b-button 
        variant="link" 
        class="p-0 text-decoration-none justify-content-start"
        @click="expandToSiteWideSearch"
      >
        <span class="small">
          <font-awesome-icon 
            :icon="['fas', 'globe']" 
            class="mr-1" 
          />
          Search entire site for "{{ searchTerm }}"
        </span>
      </b-button>
    </div>
  </div>
</template>

<script>
import { getFileTypeLabel } from '@/core/utils/fileTypeUtils';
import { searchSite } from '@/core/utils/sharePointUtils';

// Constants
const SEARCH_DEBOUNCE_MS = 300;
const CACHE_SIZE_LIMIT = 10;

export default {
  name: 'FileSearchFilter',
  props: {
    files: {
      type: Array,
      default: () => [],
    },
    siteId: {
      type: String,
      default: null,
    },
    enableSiteSearch: {
      type: Boolean,
      default: false,
    },
    currentLocationName: {
      type: String,
      default: 'this folder',
    },
  },
  data() {
    return {
      searchTerm: '',
      selectedFileType: '',
      siteSearchResults: [],
      isSearching: false,
      searchError: null,
      searchDebounceTimer: null,
      isSiteWideSearch: false, // Track if currently doing site-wide search
      siteSearchModeEnabled: false, // Track if site-wide search mode is enabled (sticky)
      searchCache: new Map(), // Cache search results to avoid repeated API calls
    };
  },
  watch: {
    // When files prop change, re-apply filters and emit filtered results
    files: {
      handler() {
        this.$nextTick(() => {
          this.emitFilterChange();
        });
      },
      immediate: true,
    },
    // Clear search cache when site changes
    siteId() {
      this.searchCache.clear();
      this.siteSearchResults = [];
    },
  },
  computed: {
    // Get files to work with - either site search results or local files
    workingFiles() {
      return this.isSiteWideSearch && this.siteSearchResults.length > 0 
        ? this.siteSearchResults 
        : this.files;
    },
    // Get available file types from current files
    availableFileTypes() {
      const typeMap = new Map();
      
      // Use files after applying search filter but before applying file type filter
      let filesToAnalyse = this.workingFiles;
      
      // Apply search term filter if doing local search (not site-wide)
      if (!this.isSiteWideSearch && this.searchTerm.trim()) {
        filesToAnalyse = this.applySearchFilter(filesToAnalyse, this.searchTerm);
      }
      
      filesToAnalyse.forEach(file => {
        const extension = this.getFileExtension(this.getFileName(file));
        if (extension) {
          const existing = typeMap.get(extension);
          if (existing) {
            existing.count++;
          } else {
            typeMap.set(extension, {
              extension,
              label: getFileTypeLabel(extension),
              count: 1,
            });
          }
        }
      });
      
      // Sort by extension alphabetically
      return Array.from(typeMap.values()).sort((a, b) => 
        a.extension.localeCompare(b.extension),
      );
    },
    // Check if any filters are currently active
    hasActiveFilters() {
      return this.searchTerm.trim() !== '' || this.selectedFileType !== '';
    },
    // Get count of files after filtering
    filteredCount() {
      return this.getFilteredFiles().length;
    },
    // Get total count of files
    totalCount() {
      return this.workingFiles.length;
    },
  },
  methods: {
    // Clear search debounce timer
    clearSearchTimer() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
        this.searchDebounceTimer = null;
      }
    },
    /**
     * Process site search results and cache them
     * @param {Array} results - Raw search results
     * @param {string} searchKey - Cache key for the search
     * @returns {Array} Processed search results
     */
    processSiteSearchResults(results, searchKey) {
      const filteredResults = results
        .filter(item => item.type === 'file' && this.getFileName(item))
        .map(item => ({
          ...item,
          // Ensure consistent naming
          displayName: this.getFileName(item),
          name: this.getFileName(item),
        }));
      
      // Cache the results (limit cache size to prevent memory issues)
      if (this.searchCache.size >= CACHE_SIZE_LIMIT) {
        // Remove oldest entry
        const firstKey = this.searchCache.keys().next().value;
        this.searchCache.delete(firstKey);
      }
      this.searchCache.set(searchKey, filteredResults);
      
      return filteredResults;
    },
    // Get the display name of a file
    getFileName(file) {
      return file.name || file.displayName || '';
    },
    // Get file extension from filename (without dot)
    getFileExtension(filename) {
      if (!filename) return '';
      const lastDot = filename.lastIndexOf('.');
      return lastDot > 0 ? filename.substring(lastDot + 1).toLowerCase() : '';
    },
    // Get file type label
    getFileTypeLabel(extension) {
      return getFileTypeLabel(extension);
    },
    // Apply search term filter to files
    applySearchFilter(files, searchTerm) {
      if (!searchTerm.trim()) return files;
      
      const searchLower = searchTerm.toLowerCase();
      return files.filter(file => {
        const fileName = this.getFileName(file).toLowerCase();
        return fileName.includes(searchLower);
      });
    },
    // Reset all search-related state
    resetSearchState() {
      this.searchTerm = '';
      this.siteSearchResults = [];
      this.isSiteWideSearch = false;
      this.siteSearchModeEnabled = false;
      this.searchError = null;
      this.clearSearchTimer();
    },
    // Schedule a site-wide search with debouncing
    scheduleSiteSearch() {
      this.searchDebounceTimer = setTimeout(() => {
        this.isSiteWideSearch = true;
        this.performSiteSearch();
      }, SEARCH_DEBOUNCE_MS);
    },
    // Apply filters consistently to files
    applyFilters(files, applySearchFilter = true) {
      let filtered = files;
      
      // Apply file type filter
      if (this.selectedFileType) {
        filtered = filtered.filter(file => {
          const extension = this.getFileExtension(this.getFileName(file));
          return extension === this.selectedFileType;
        });
      }
      
      // Apply search term filter if requested
      if (applySearchFilter && this.searchTerm.trim()) {
        filtered = this.applySearchFilter(filtered, this.searchTerm);
      }
      
      return filtered;
    },
    // Filter files based on current search term and file type
    getFilteredFiles() {
      // For site-wide search, the API already returned matching files, so no need to apply search filter
      const applySearchFilter = !this.isSiteWideSearch;
      return this.applyFilters(this.workingFiles, applySearchFilter);
    },
    // Handle search input change
    onSearchChange() {
      // Clear previous search timer
      this.clearSearchTimer();
      
      // If search term is empty, reset everything
      if (!this.searchTerm.trim()) {
        this.isSiteWideSearch = false;
        this.siteSearchResults = [];
        this.searchError = null;
        this.emitFilterChange();
        return;
      }
      
      // If site-wide search mode is enabled (sticky), perform site-wide search
      if (this.siteSearchModeEnabled) {
        this.scheduleSiteSearch();
      } else if (this.files.length === 0 && this.enableSiteSearch) {
        // If no local files and site search is enabled, automatically search site-wide
        this.searchDebounceTimer = setTimeout(() => {
          this.isSiteWideSearch = true;
          this.siteSearchModeEnabled = true; // Enable sticky mode
          this.performSiteSearch();
        }, SEARCH_DEBOUNCE_MS);
      } else {
        // Reset site-wide search when search term changes (only if not in sticky mode)
        this.isSiteWideSearch = false;
        this.siteSearchResults = [];
        this.searchError = null;
        
        // Always start with local search
        this.emitFilterChange();
      }
    },
    // Select a file type from dropdown
    selectFileType(fileType) {
      this.selectedFileType = fileType;
      this.emitFilterChange();
    },
    // Clear only the file type filter (keep search term)
    clearFileTypeFilter() {
      this.selectedFileType = '';
      this.emitFilterChange();
    },
    // Clear search term
    clearSearch() {
      this.resetSearchState();
      this.emitFilterChange();
    },
    // Emit filtered files to parent component
    emitFilterChange() {
      const filteredFiles = this.getFilteredFiles();
      this.$emit('files-filtered', {
        filteredFiles,
        hasActiveFilters: this.hasActiveFilters,
        searchTerm: this.searchTerm,
        selectedFileType: this.selectedFileType,
        isSiteSearch: this.isSiteWideSearch,
        siteSearchModeEnabled: this.siteSearchModeEnabled,
        isSearching: this.isSearching,
        searchError: this.searchError,
      });
    },
    // Perform site-wide search using SharePoint API
    async performSiteSearch() {
      if (!this.siteId || !this.searchTerm.trim()) return;
      
      const searchKey = `${this.siteId}-${this.searchTerm.toLowerCase()}`;
      
      // Check cache first
      if (this.searchCache.has(searchKey)) {
        this.siteSearchResults = this.searchCache.get(searchKey);
        this.emitFilterChange();
        return;
      }
      
      this.isSearching = true;
      this.searchError = null;
      
      try {
        const results = await searchSite(this.siteId, this.searchTerm);
        
        // Process and cache the results
        this.siteSearchResults = this.processSiteSearchResults(results, searchKey);
        this.emitFilterChange();
      } catch (error) {
        this.searchError = error.message || 'Search failed';
        this.siteSearchResults = [];
        this.emitFilterChange();
      } finally {
        this.isSearching = false;
      }
    },
    // Expand search to site-wide
    expandToSiteWideSearch() {
      if (!this.enableSiteSearch || !this.siteId || !this.searchTerm.trim()) return;
      
      this.isSiteWideSearch = true;
      this.siteSearchModeEnabled = true; // Enable sticky mode
      this.performSiteSearch();
    },
  },
  beforeDestroy() {
    // Clean up search timer
    this.clearSearchTimer();
  },
};
</script>

<style lang="scss" scoped>
.file-search-filter {
  // Custom compact sizing for matching breadcrumb height
  .compact-search {
    height: 2rem;
    
    .form-control, .compact-input {
      height: 2rem !important;
      font-size: 0.75rem !important;
      padding: 0.25rem 0.5rem !important;
    }
    
    .input-group-text {
      font-size: 0.75rem !important;
      padding: 0.25rem 0.5rem !important;
      height: 2rem !important;
    }
    
    .btn {
      font-size: 0.75rem !important;
      padding: 0.25rem 0.5rem !important;
      height: 2rem !important;
    }
  }
  
  // Match dropdown button height with breadcrumbs
  ::v-deep .compact-dropdown {
    height: 2rem !important;
    font-size: 0.75rem !important;
    padding: 0.25rem 0.5rem !important;
    line-height: 1 !important;
  }
  
  ::v-deep .dropdown-toggle {
    height: 2rem !important;
    font-size: 0.75rem !important;
    padding: 0.25rem 0.5rem !important;
  }
  
  // Filter clear button styling
  .filter-clear-btn {
    font-size: 0.75rem !important;
    line-height: 1 !important;
  }
}
</style>
